/**
 * Primitivas de UI do painel — pequenas, puras, sem acesso a dados.
 *
 * Ficam juntas porque cada uma é curta e são sempre usadas em conjunto; separar
 * em oito arquivos de dez linhas seria indireção sem ganho.
 */
import type { ReactNode } from 'react';
import { IconAlert, IconInbox } from './icons.js';
import type { Tone } from '../lib/format.js';

/* ------------------------------------------------------------------ Button */

type ButtonVariant = 'primary' | 'ghost' | 'subtle' | 'danger' | 'danger-ghost';

interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  type?: 'button' | 'submit';
  disabled?: boolean;
  loading?: boolean;
  size?: 'sm' | 'md';
  block?: boolean;
  title?: string;
  autoFocus?: boolean;
}

export function Button({
  children,
  onClick,
  variant = 'subtle',
  type = 'button',
  disabled,
  loading,
  size = 'md',
  block,
  title,
  autoFocus,
}: ButtonProps) {
  const cls = [
    'btn',
    `btn--${variant}`,
    size === 'sm' ? 'btn--sm' : '',
    block ? 'btn--block' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      autoFocus={autoFocus}
      type={type}
      className={cls}
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
    >
      {loading && <span className="spinner" style={{ width: 14, height: 14 }} />}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------- Badge */

export function Badge({ tone = 'neutral', dot, children }: { tone?: Tone; dot?: boolean; children: ReactNode }) {
  return (
    <span className={`badge badge--${tone}`}>
      {dot && <span className="badge__dot" />}
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------- Card */

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={`card${className ? ` ${className}` : ''}`}>{children}</section>;
}

export function CardHead({
  title,
  sub,
  actions,
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="card__head">
      <div>
        <div className="card__title">{title}</div>
        {sub && <div className="card__sub">{sub}</div>}
      </div>
      {actions && <div className="card__head-actions">{actions}</div>}
    </div>
  );
}

/* -------------------------------------------------------------- PageHeader */

export function PageHeader({
  title,
  desc,
  actions,
}: {
  title: string;
  desc?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div className="page-head__text">
        <h1 className="page-head__title">{title}</h1>
        {desc && <p className="page-head__desc">{desc}</p>}
      </div>
      {actions && <div className="page-head__actions">{actions}</div>}
    </div>
  );
}

/* --------------------------------------------------------------- Stat card */

/** Segmento de uma barra empilhada (dado real: sem inventar proporção). */
export interface BarSeg {
  label: string;
  value: number;
  tone: 'info' | 'warn' | 'ok' | 'danger';
}

/** Chip de status ao lado do valor — texto vem de estado real, não de tendência. */
export interface StatDelta {
  text: string;
  tone: 'ok' | 'danger' | 'muted';
}

export function Stat({
  label,
  value,
  foot,
  accent = 'info',
  icon,
  wide,
  delta,
  bar,
  children,
}: {
  label: ReactNode;
  value: ReactNode;
  foot?: ReactNode;
  accent?: 'ok' | 'warn' | 'danger' | 'info';
  icon?: ReactNode;
  wide?: boolean;
  delta?: StatDelta;
  bar?: BarSeg[];
  children?: ReactNode;
}) {
  return (
    <div className={`card stat${wide ? ' stat--wide' : ''}`}>
      <span className={`stat__accent stat__accent--${accent}`} />
      <div className="stat__label">
        {icon}
        {label}
      </div>
      <div className="stat__head">
        <div className="stat__value">{value}</div>
        {delta && <span className={`stat__delta stat__delta--${delta.tone}`}>{delta.text}</span>}
      </div>
      {bar && bar.some((s) => s.value > 0) && <StackBar segments={bar} />}
      {foot && <div className="stat__foot">{foot}</div>}
      {children}
    </div>
  );
}

/** Barra empilhada proporcional + legenda. Larguras somam o total real. */
export function StackBar({ segments }: { segments: BarSeg[] }) {
  const total = segments.reduce((soma, s) => soma + s.value, 0) || 1;
  return (
    <>
      <div
        className="stackbar"
        role="img"
        aria-label={segments.map((s) => `${s.label}: ${s.value}`).join(', ')}
      >
        {segments
          .filter((s) => s.value > 0)
          .map((s) => (
            <span
              key={s.label}
              className={`stackbar__seg stackbar__seg--${s.tone}`}
              style={{ width: `${(s.value / total) * 100}%` }}
            />
          ))}
      </div>
      <div className="stackbar-legend">
        {segments
          .filter((s) => s.value > 0)
          .map((s) => (
            <span key={s.label}>
              <i className={`stackbar__seg--${s.tone}`} />
              {s.label} <b className="mono">{s.value}</b>
            </span>
          ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------- StatusDot */

export function StatusDot({ up }: { up: boolean }) {
  return <span className={`sdot ${up ? 'sdot--up' : 'sdot--down'}`} title={up ? 'no ar' : 'fora'} />;
}

/* --------------------------------------------------------------- Spinner */

export function Spinner({ large }: { large?: boolean }) {
  return <span className={`spinner${large ? ' spinner--lg' : ''}`} />;
}

/* --------------------------------------------------- estados de carga/vazio */

/** Uma nota da pilha de contagem. */
function Bill() {
  return (
    <span className="money-count__bill">
      <svg width="76" height="42" viewBox="0 0 76 42" fill="none">
        <rect x="1" y="1" width="74" height="40" rx="5" fill="#26a268" stroke="#4fd89b" strokeWidth="1" />
        <rect x="6" y="6" width="64" height="30" rx="3" fill="none" stroke="rgb(255 255 255 / 0.22)" />
        <circle cx="38" cy="21" r="8.5" fill="rgb(255 255 255 / 0.1)" stroke="rgb(255 255 255 / 0.32)" />
        <text x="38" y="25" textAnchor="middle" fontSize="10" fontWeight="700" fill="#eafff4" fontFamily="system-ui, sans-serif">
          R$
        </text>
      </svg>
    </span>
  );
}

/** "Contando dinheiro" — o loader das telas de carga (tema de cobrança). */
export function MoneyLoader() {
  return (
    <span className="money-count" role="img" aria-label="contando">
      <Bill />
      <Bill />
      <Bill />
      <Bill />
      <Bill />
    </span>
  );
}

export function Loading({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div className="loading-block">
      <MoneyLoader />
      <span>{label}</span>
    </div>
  );
}

export function ErrorBlock({ message }: { message: string }) {
  return (
    <div className="error-block" role="alert">
      <IconAlert size={18} style={{ flex: 'none', marginTop: 1 }} />
      <span>{message}</span>
    </div>
  );
}

export function EmptyState({
  title,
  desc,
  icon,
  action,
}: {
  title: string;
  desc?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty__icon">{icon ?? <IconInbox size={42} />}</span>
      <div className="empty__title">{title}</div>
      {desc && <p className="empty__desc">{desc}</p>}
      {action}
    </div>
  );
}

/* -------------------------------------------------------------------- KV */

export function KV({ children }: { children: ReactNode }) {
  return <dl className="kv">{children}</dl>;
}

export function KVRow({ k, children }: { k: ReactNode; children: ReactNode }) {
  return (
    <>
      <dt className="kv__k">{k}</dt>
      <dd className="kv__v">{children}</dd>
    </>
  );
}

/** Travessão para valor ausente do ERP — distinto de vazio. */
export function Dash() {
  return <span className="dash">—</span>;
}
